/**
 * D6 (microUSDC) helpers — bigint-backed arithmetic on base-10 strings.
 *
 * USDC has 6 decimals. The wire format is a base-10 string (preserves
 * precision past 53 bits). Every monetary running total — `volumeTotal`,
 * `prizePool`, `totalReserves`, etc. — is a D6 string in API responses
 * and NATS payloads. JS `number` corrupts these above ~9.0e15 (2^53), so
 * arithmetic must go through `bigint`.
 *
 * Used by codegen-emitted realtime reducer recipes:
 *
 *   add: { volumeTotal: payload.sizeD6 }
 *       → calls addD6String(prev.volumeTotal, ev.payload.sizeD6)
 */

/** Coerce an unknown to a D6 base-10 string. Returns '0' for nullish/invalid. */
export function coerceD6String(v: unknown): string {
  if (v === undefined || v === null) return '0';
  if (typeof v === 'string') return v;
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return '0';
    if (Number.isInteger(v)) return String(v);
    return String(Math.trunc(v));
  }
  if (typeof v === 'bigint') return v.toString();
  return '0';
}

/**
 * Add a D6 delta to a D6 running total, both as base-10 strings.
 * Returns the new total as a base-10 string. Bigint internally.
 */
export function addD6String(prev: unknown, delta: unknown): string {
  const a = parseBigintOrZero(prev);
  const b = parseBigintOrZero(delta);
  return (a + b).toString();
}

/** Returns true when the coerced D6 value is exactly zero. */
export function isZeroD6(v: unknown): boolean {
  return parseBigintOrZero(v) === 0n;
}

function parseBigintOrZero(v: unknown): bigint {
  if (typeof v === 'bigint') return v;
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return 0n;
    return BigInt(Math.trunc(v));
  }
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (trimmed === '' || !/^-?\d+$/.test(trimmed)) return 0n;
    try {
      return BigInt(trimmed);
    } catch {
      return 0n;
    }
  }
  return 0n;
}
